"use client";
/**
 * Shared `useStaffShiftNeighborDays` hook (Mobile P0.8).
 *
 * Finds the latest work day before and earliest work day after a selected day
 * where a staff member has hours entries, within a bounded window. RN-compatible:
 * no DOM/Next imports, uses the global `fetch`. The API host is injected via
 * `baseUrl` so web keeps relative URLs while mobile points at its API origin.
 *
 * Marked `"use client"` so Next.js treats it as a client boundary when reached
 * through the `@tippo/data` barrel from a Server Component graph.
 */
import { useCallback, useEffect, useState } from "react";
import { addDays, format, parseISO, subDays } from "date-fns";
/**
 * Picks the nearest work-day neighbors around `selectedKey` from a set of
 * `YYYY-MM-DD` work dates. Pure and exported for testing/reuse.
 */
export function findNeighborDays(workDates, selectedKey) {
    const unique = [...new Set(workDates)].sort();
    let prevDay = null;
    for (const d of unique) {
        if (d < selectedKey)
            prevDay = d;
        else
            break;
    }
    const nextDay = unique.find((d) => d > selectedKey) ?? null;
    return { prevDay, nextDay };
}
/**
 * Finds the latest work day before and earliest work day after `selectedKey`
 * where the staff member has hours entries (within a bounded window).
 */
export function useStaffShiftNeighborDays({ restaurantId, staffProfileId, selectedKey, lookbackDays = 120, lookaheadDays = 120, enabled = true, baseUrl = "", }) {
    const [prevDay, setPrevDay] = useState(null);
    const [nextDay, setNextDay] = useState(null);
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const anchor = parseISO(`${selectedKey}T12:00:00`);
            const start = format(subDays(anchor, lookbackDays), "yyyy-MM-dd");
            const end = format(addDays(anchor, lookaheadDays), "yyyy-MM-dd");
            const res = await fetch(`${baseUrl}/api/restaurants/${restaurantId}/hours?periodStart=${start}&periodEnd=${end}`);
            const data = (await res.json().catch(() => ({})));
            const list = res.ok
                ? (data.hours ?? [])
                : [];
            const mine = list
                .filter((r) => r.staff_profile_id === staffProfileId)
                .map((r) => String(r.work_date).slice(0, 10));
            const { prevDay: prev, nextDay: next } = findNeighborDays(mine, selectedKey);
            setPrevDay(prev);
            setNextDay(next);
        }
        finally {
            setLoading(false);
        }
    }, [
        restaurantId,
        staffProfileId,
        selectedKey,
        lookbackDays,
        lookaheadDays,
        baseUrl,
    ]);
    useEffect(() => {
        if (!enabled) {
            setPrevDay(null);
            setNextDay(null);
            setLoading(false);
            return;
        }
        void load();
    }, [load, enabled]);
    return { prevDay, nextDay, loading, refresh: load };
}
